import type { TipSheetPlayer, TipSheetTeam, TipVerdict } from '../lib/api.ts';
import { points } from '../lib/format.ts';

/**
 * One team's page of the Tip Sheet: the summary, every player with a verdict
 * and a note, then the swaps and the waiver targets.
 *
 * The rows are the scoreboard's lineup rows with two additions, the opponent
 * chip and the verdict badge, so a reader who knows one knows the other.
 */

const VERDICT_STYLE: Record<TipVerdict, string> = {
  start: 'bg-cash/15 text-cash',
  flex: 'bg-brass/15 text-brass',
  sit: 'bg-blood/15 text-blood',
  out: 'bg-blood/15 text-blood',
  bye: 'bg-felt-line text-chalk-dim',
};

export function VerdictBadge({ verdict }: { verdict: TipVerdict }) {
  return (
    <span
      className={`inline-block shrink-0 rounded-full px-1.5 py-px font-mono text-[0.6rem] uppercase tracking-wider ${VERDICT_STYLE[verdict]}`}
    >
      {verdict}
    </span>
  );
}

function opponentText(p: TipSheetPlayer): string {
  if (p.verdict === 'bye' || (p.opponent === null && p.home === null)) {
    return p.opponent === null && p.verdict !== 'bye' ? '?' : 'BYE';
  }
  return `${p.home ? 'vs' : '@'} ${p.opponent}`;
}

function PlayerRow({ player }: { player: TipSheetPlayer }) {
  const meta = player.slot === player.position ? player.proTeam : `${player.position} ${player.proTeam}`;
  return (
    <div className="border-b border-felt-line py-2 last:border-b-0">
      <div className="flex items-baseline gap-2 font-mono text-xs">
        <span className="w-9 shrink-0 text-[0.65rem] uppercase tracking-wider text-chalk-dim">
          {player.slot}
        </span>
        <span className="min-w-0 flex-1 truncate font-body text-chalk">
          {player.name}
          {player.injury && (
            <span className="ml-1 text-[0.6rem] uppercase text-blood">{player.injury.slice(0, 1)}</span>
          )}
          <span className="ml-1.5 text-[0.6rem] uppercase tracking-wide text-chalk-dim">{meta}</span>
        </span>
        <span className="w-14 shrink-0 text-right text-[0.65rem] uppercase tracking-wide text-chalk-dim">
          {opponentText(player)}
        </span>
        <span className="w-10 shrink-0 text-right tabular text-chalk-dim">{points(player.projected)}</span>
        <span className="w-12 shrink-0 text-right">
          <VerdictBadge verdict={player.verdict} />
        </span>
      </div>
      {player.note && (
        <p className="mt-1 pl-11 font-editorial text-sm leading-snug text-chalk">
          {player.note}
          {player.sources.map((url, i) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={url}
              className="ml-1 font-mono text-[0.6rem] text-chalk-dim hover:text-brass"
            >
              ↗{player.sources.length > 1 ? i + 1 : ''}
            </a>
          ))}
        </p>
      )}
    </div>
  );
}

export function TipSheetTeamCard({ team, nameOf }: { team: TipSheetTeam; nameOf: (id: string) => string }) {
  const byId = new Map(team.players.map((p) => [p.playerId, p]));
  const starters = team.players.filter((p) => p.started);
  const bench = team.players.filter((p) => !p.started);

  return (
    <div className="space-y-5">
      {team.error && (
        <p className="font-mono text-xs text-blood">
          The sheet for {nameOf(team.ownerId)} didn't get written: {team.error}
        </p>
      )}
      {team.summary && (
        <p className="max-w-prose font-editorial text-lg leading-relaxed text-chalk">{team.summary}</p>
      )}

      <div>
        <p className="eyebrow mb-1">Starters</p>
        {starters.map((p) => (
          <PlayerRow key={p.playerId} player={p} />
        ))}
      </div>

      {bench.length > 0 && (
        <div className="opacity-75">
          <p className="eyebrow mb-1">Bench</p>
          {bench.map((p) => (
            <PlayerRow key={p.playerId} player={p} />
          ))}
        </div>
      )}

      {team.swaps.length > 0 && (
        <div>
          <p className="eyebrow mb-1">Swaps</p>
          <ul className="space-y-2">
            {team.swaps.map((s) => (
              <li key={`${s.benchPlayerId}-${s.forPlayerId}`} className="text-sm">
                <span className="font-display font-bold">
                  Start {byId.get(s.benchPlayerId)?.name ?? '?'}
                </span>
                <span className="text-chalk-dim"> over </span>
                <span className="font-display font-bold">{byId.get(s.forPlayerId)?.name ?? '?'}</span>
                <span className="block font-editorial text-sm text-chalk">{s.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {team.waivers.length > 0 && (
        <div>
          <p className="eyebrow mb-1">Waiver wire</p>
          <ul className="space-y-2">
            {team.waivers.map((w) => (
              <li key={w.playerId} className="text-sm">
                <span className="font-display font-bold">{w.name}</span>
                <span className="ml-1.5 font-mono text-[0.65rem] uppercase tracking-wide text-chalk-dim">
                  {w.position} {w.proTeam} · proj {points(w.projected)} · {w.percentOwned}% owned
                </span>
                <span className="block font-editorial text-sm text-chalk">{w.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {team.incomplete && !team.error && (
        <p className="font-mono text-[0.65rem] text-chalk-dim">
          Some players came back without a note; their verdicts are the defaults.
        </p>
      )}
    </div>
  );
}
